import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ErrorAlert } from "./ErrorAlert";

/** Confirm something that cannot be undone, with a reason on the record. `consequence` names what
 * will happen in one sentence; money and overrides always come through here. `optional` lets the
 * text be left empty (a note, not a reason). `initialReason` starts the box with a suggestion the
 * person edits (mount with a `key` to start it again). */
export function ReasonDialog({
  open,
  onOpenChange,
  title,
  consequence,
  confirmLabel,
  reasonLabel = "Reason",
  destructive,
  optional,
  initialReason = "",
  maxLength,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  consequence: string;
  confirmLabel: string;
  reasonLabel?: string;
  destructive?: boolean;
  optional?: boolean;
  initialReason?: string;
  maxLength?: number;
  onConfirm: (reason: string) => Promise<unknown> | unknown;
}) {
  const [reason, setReason] = useState(initialReason);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const trimmed = reason.trim();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!trimmed && !optional) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm(trimmed);
      setReason("");
      onOpenChange(false);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!busy) onOpenChange(o);
      }}
    >
      <DialogContent>
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{consequence}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="reason-dialog-text">{reasonLabel}</Label>
            <Textarea
              id="reason-dialog-text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required={!optional}
              rows={3}
              maxLength={maxLength}
              autoFocus
            />
          </div>
          {error != null && <ErrorAlert error={error} />}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant={destructive ? "destructive" : "default"}
              disabled={(!trimmed && !optional) || busy}
            >
              {busy ? "Working…" : confirmLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
